/* =========================================================================
   useMenu — generic floating context menu system
   Matches HTML lines 998-1004 exactly.
   ========================================================================= */

import { useContext, useState, useCallback } from 'react';
import { MenuItem, MenuState } from '../lib/dashboard-types';
import { CommandCenterStateProviderContext } from '../../../../context';

interface OpenMenuOpts {
  atPointer?: boolean;
  width?: number;
  title?: string;
  triggerId?: string;
  keepPosition?: boolean;
  onBack?: () => void;
  anchorX?: number;
  anchorY?: number;
  alignRight?: boolean;
}

export function useMenu() {
  const context = useContext(CommandCenterStateProviderContext);
  const [localMenu, setLocalMenu] = useState<MenuState | null>(null);

  const menu = context ? context.telemetryMenuState : localMenu;
  const setMenu = context ? context.setTelemetryMenuState : setLocalMenu;

  const openMenu = useCallback(
    (e: React.MouseEvent, items: MenuItem[], opts?: OpenMenuOpts) => {
      const o = opts || {};
      const target = (e?.currentTarget || e?.target) as HTMLElement;
      const r = target && typeof target.getBoundingClientRect === 'function'
        ? target.getBoundingClientRect()
        : { left: e?.clientX || 0, right: (e?.clientX || 0) + 100, bottom: e?.clientY || 0 };
      const mw = o.width || 200;
      let x = o.anchorX != null
        ? o.anchorX
        : o.atPointer
          ? e.clientX
          : o.alignRight && r.right
            ? r.right - mw
            : r.left;
      let y = o.anchorY != null
        ? o.anchorY
        : o.atPointer
          ? e.clientY
          : r.bottom + 4;
      if (o.keepPosition && menu) {
        x = menu.x;
        y = menu.y;
      }
      if (x + mw > window.innerWidth - 8) x = window.innerWidth - mw - 8;
      if (x < 8) x = 8;
      const triggerId = o.triggerId || (target ? target.id : null);
      setMenu({ items, x, y, width: mw, title: o.title || null, triggerId, onBack: o.onBack });
    },
    [setMenu, menu]
  );

  const closeMenu = useCallback(() => {
    setMenu(null);
  }, [setMenu]);

  const updateMenuItems = useCallback(
    (items: MenuItem[]) => {
      setMenu((prev) => (prev ? { ...prev, items } : null));
    },
    [setMenu]
  );

  return { menu, openMenu, closeMenu, updateMenuItems };
}

export default useMenu;
